import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Receipt, Search, Plus, Check, Building2, X, Calculator, DollarSign, CreditCard, Upload, Trash2, ChevronDown, ChevronsUpDown, AlertTriangle, FileText, Sparkles, Filter, CheckCheck, RotateCcw, Copy, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Normalizar texto para búsqueda insensible a mayúsculas y acentos
const normalizarTexto = (texto) =>
  (texto || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

// Componente de selección y búsqueda de proveedores para la cartola
const SelectorProveedorCartola = ({
  valorId,
  nombreCartola,
  nombreEditable,
  proveedores,
  categorias,
  disabled,
  onSelectProveedor,
  onMarcarNuevo
}) => {
  const [abierto, setAbierto] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (abierto) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    } else {
      setBusqueda('');
    }
  }, [abierto]);

  const esNuevo = !valorId || valorId === 'nuevo';
  const proveedorSeleccionado = proveedores.find(p => p.id_proveedor === valorId);

  // Filtrado por coincidencia de texto (multi-palabra, insensible a mayúsculas y tildes)
  const provFiltrados = useMemo(() => {
    const q = normalizarTexto(busqueda);
    if (!q) return proveedores;
    const palabras = q.split(/\s+/).filter(Boolean);
    return proveedores.filter(p => {
      const nom = normalizarTexto(p.nombre);
      return palabras.every(palabra => nom.includes(palabra));
    });
  }, [proveedores, busqueda]);

  return (
    <Popover open={abierto} onOpenChange={(open) => {
      if (!disabled) setAbierto(open);
    }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setAbierto(true)}
          className={`w-full text-xs border rounded px-2 py-1 h-[28px] flex items-center justify-between text-left transition-all ${
            disabled
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200 opacity-60'
              : 'bg-white hover:border-purple-400 border-gray-300 focus:outline-none focus:ring-1 focus:ring-purple-500'
          }`}
          title={esNuevo ? '+ Crear nuevo' : (proveedorSeleccionado?.nombre || nombreEditable || 'Seleccionar...')}
        >
          <span className="truncate flex-1 mr-1 text-[11px]">
            {esNuevo ? (
              <span className="text-purple-700 font-semibold flex items-center gap-1">
                <Plus className="w-3 h-3 text-purple-600 shrink-0" />
                Crear nuevo
              </span>
            ) : (
              <span className="text-gray-800 font-medium">
                {proveedorSeleccionado ? proveedorSeleccionado.nombre : (nombreEditable || 'Seleccionar...')}
              </span>
            )}
          </span>
          <ChevronsUpDown className="w-3 h-3 text-gray-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-80 z-[70] shadow-xl border border-purple-200 bg-white rounded-lg overflow-hidden"
        align="start"
        sideOffset={4}
      >
        {/* Buscador de proveedores por coincidencia */}
        <div className="p-2 border-b bg-gray-50 flex items-center gap-2">
          <Search className="w-3.5 h-3.5 text-purple-600 shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar proveedor..."
            className="w-full text-xs bg-transparent border-0 outline-none p-0 focus:ring-0 placeholder:text-gray-400 text-gray-800"
          />
          {busqueda && (
            <button
              type="button"
              onClick={() => {
                setBusqueda('');
                inputRef.current?.focus();
              }}
              className="text-gray-400 hover:text-gray-600 p-0.5 rounded"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>

        {/* Opción Crear Nuevo */}
        <div className="p-1 border-b bg-purple-50/50">
          <button
            type="button"
            onClick={() => {
              onMarcarNuevo();
              setAbierto(false);
            }}
            className={`w-full text-left px-2.5 py-1.5 text-xs rounded-md flex items-center justify-between transition-colors ${
              esNuevo
                ? 'bg-purple-100 text-purple-900 font-bold'
                : 'text-purple-700 hover:bg-purple-100/70 font-medium'
            }`}
          >
            <div className="flex items-center gap-2 min-w-0">
              <Plus className="w-3.5 h-3.5 text-purple-600 shrink-0" />
              <div className="flex flex-col truncate">
                <span className="truncate">+ Crear como nuevo proveedor</span>
                <span className="text-[10px] text-purple-600/80 truncate">
                  Usar: {nombreEditable || nombreCartola}
                </span>
              </div>
            </div>
            {esNuevo && <Check className="w-4 h-4 text-purple-700 shrink-0 ml-1" />}
          </button>
        </div>

        {/* Listado ordenado alfabéticamente y filtrable */}
        <div className="max-h-56 overflow-y-auto p-1 divide-y divide-gray-50">
          <div className="px-2.5 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider flex justify-between items-center bg-gray-50/40 rounded">
            <span>Proveedores A-Z</span>
            <span>{provFiltrados.length}</span>
          </div>

          {provFiltrados.length === 0 ? (
            <div className="py-6 px-3 text-center text-xs text-gray-500">
              <p className="font-medium text-gray-700">Sin proveedores coincidentes</p>
              <p className="text-[11px] text-gray-400 mt-1">
                No hay resultados para "{busqueda}"
              </p>
              <button
                type="button"
                onClick={() => {
                  onMarcarNuevo();
                  setAbierto(false);
                }}
                className="mt-2 text-xs text-purple-600 hover:text-purple-800 font-medium underline"
              >
                Crear "{busqueda}" como nuevo
              </button>
            </div>
          ) : (
            provFiltrados.map((prov) => {
              const estaSeleccionado = valorId === prov.id_proveedor;
              const cat = categorias.find(c => c.id_categoria === prov.categoria_id);
              return (
                <button
                  key={prov.id_proveedor}
                  type="button"
                  onClick={() => {
                    onSelectProveedor(prov.id_proveedor);
                    setAbierto(false);
                  }}
                  className={`w-full text-left px-2.5 py-1.5 text-xs rounded-md flex items-center justify-between gap-1 transition-colors ${
                    estaSeleccionado
                      ? 'bg-purple-100 text-purple-900 font-bold'
                      : 'text-gray-700 hover:bg-purple-50'
                  }`}
                >
                  <div className="flex flex-col min-w-0 pr-1">
                    <span className="truncate font-medium">{prov.nombre}</span>
                    {cat && (
                      <span className="text-[10px] text-gray-400 truncate">
                        {cat.nombre}
                      </span>
                    )}
                  </div>
                  {estaSeleccionado && (
                    <Check className="w-4 h-4 text-purple-700 shrink-0 ml-1" />
                  )}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const GastoRapido = () => {
  // Estados para selección de propiedad
  const [propiedades, setPropiedades] = useState([]);
  const [propiedadSeleccionada, setPropiedadSeleccionada] = useState(null);
  const [cargandoPropiedades, setCargandoPropiedades] = useState(true);

  // Estados para el formulario de gasto
  const [categorias, setCategorias] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [mediosPago, setMediosPago] = useState([]);
  
  const [formData, setFormData] = useState({
    fecha: new Date().toISOString().split('T')[0],
    monto: '',
    categoria_id: '',
    proveedor_id: '',
    proveedor: '',
    medio_pago_id: '',
    notas: ''
  });

  // Autocompletado proveedor
  const [proveedorBusqueda, setProveedorBusqueda] = useState('');
  const [mostrarSugerencias, setMostrarSugerencias] = useState(false);
  const inputRef = useRef(null);
  const sugerenciasRef = useRef(null);

  // Modal nuevo proveedor
  const [mostrarModalProveedor, setMostrarModalProveedor] = useState(false);
  const [nuevoProveedorNombre, setNuevoProveedorNombre] = useState('');
  const [nuevoProveedorCategoria, setNuevoProveedorCategoria] = useState('');
  
  // Modal nueva categoría
  const [mostrarModalCategoria, setMostrarModalCategoria] = useState(false);
  const [nuevaCategoriaNombre, setNuevaCategoriaNombre] = useState('');

  const [guardando, setGuardando] = useState(false);
  const [gastoCreado, setGastoCreado] = useState(false);

  // Estados para el cálculo de monto facturado
  const [mostrarCalculadora, setMostrarCalculadora] = useState(false);
  const [formFacturado, setFormFacturado] = useState({
    monto: '',
    fecha: new Date().toISOString().split('T')[0],
    medio_pago_id: ''
  });
  const [resultadoCalculo, setResultadoCalculo] = useState(null);
  const [gastosDelMes, setGastosDelMes] = useState([]);
  const [calculando, setCalculando] = useState(false);
  const [agregandoDiferencia, setAgregandoDiferencia] = useState(false);

  // Estados para procesamiento de cartola
  const [mostrarModalCartola, setMostrarModalCartola] = useState(false);
  const [procesandoCartola, setProcesandoCartola] = useState(false);
  const [cartolaData, setCartolaData] = useState(null);
  const [gastosCartolaSeleccionados, setGastosCartolaSeleccionados] = useState({});
  const [categoriaCartola, setCategoriaCartola] = useState('');
  const [fechaGlobalCartola, setFechaGlobalCartola] = useState(new Date().toISOString().split('T')[0]); // Fecha única para todos los gastos
  const [creandoGastosCartola, setCreandoGastosCartola] = useState(false);
  const [proveedoresCartola, setProveedoresCartola] = useState({}); // Map de proveedor -> {id, nombre_editable, crear_nuevo, categoria_id}
  const [mostrarTodasCategorias, setMostrarTodasCategorias] = useState({}); // Map de proveedor -> boolean para ver todas
  const fileInputRef = useRef(null);

  // Estados para procesamiento de movimientos pegados (banco / no facturados)
  const [mostrarModalMovimientos, setMostrarModalMovimientos] = useState(false);
  const [textoMovimientos, setTextoMovimientos] = useState('');
  const [procesandoMovimientos, setProcesandoMovimientos] = useState(false);
  const [movimientosData, setMovimientosData] = useState([]);
  const [filtroMovimientos, setFiltroMovimientos] = useState('todos'); // 'todos' | 'listos' | 'duplicados'
  const [medioPagoGlobalMovimientos, setMedioPagoGlobalMovimientos] = useState('');
  const [propiedadIdMovimientos, setPropiedadIdMovimientos] = useState('');
  const [creandoGastosMovimientos, setCreandoGastosMovimientos] = useState(false);
  const [mostrarTodasCategoriasMov, setMostrarTodasCategoriasMov] = useState({});
  const [ordenCampo, setOrdenCampo] = useState('fecha'); // 'fecha' | 'monto'
  const [ordenDireccion, setOrdenDireccion] = useState('desc'); // 'desc' | 'asc'

  // Cargar propiedades al inicio
  useEffect(() => {
    cargarPropiedades();
  }, []);

  // Cargar datos cuando se selecciona propiedad
  useEffect(() => {
    if (propiedadSeleccionada) {
      cargarDatosPropiedad();
    }
  }, [propiedadSeleccionada]);

  // Cerrar sugerencias al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sugerenciasRef.current && !sugerenciasRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setMostrarSugerencias(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const cargarPropiedades = async () => {
    try {
      const res = await axios.get(`${API}/propiedades`);
      setPropiedades(res.data);
    } catch (error) {
      toast.error('Error al cargar propiedades');
    } finally {
      setCargandoPropiedades(false);
    }
  };

  const cargarDatosPropiedad = async () => {
    try {
      const [resCat, resProv, resMedios] = await Promise.all([
        axios.get(`${API}/categorias`),
        axios.get(`${API}/proveedores`),
        axios.get(`${API}/medios-pago`)
      ]);
      
      const catsOrdenadas = [...resCat.data].sort((a, b) =>
        (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' })
      );
      setCategorias(catsOrdenadas);
      const provsOrdenados = [...resProv.data].sort((a, b) =>
        (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' })
      );
      setProveedores(provsOrdenados);
      setMediosPago(resMedios.data.filter(m => m.activo !== false));
    } catch (error) {
      toast.error('Error al cargar datos');
    }
  };

  // Proveedores ordenados alfabéticamente A-Z
  const proveedoresOrdenados = useMemo(() => {
    return [...proveedores].sort((a, b) =>
      (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' })
    );
  }, [proveedores]);

  // Categorías ordenadas alfabéticamente A-Z
  const categoriasOrdenadas = useMemo(() => {
    return [...categorias].sort((a, b) =>
      (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' })
    );
  }, [categorias]);

  // Filtrar proveedores por búsqueda con coincidencia de texto
  const proveedoresSugeridos = proveedorBusqueda.trim().length > 0
    ? proveedoresOrdenados.filter(p => {
        const query = normalizarTexto(proveedorBusqueda);
        const palabras = query.split(/\s+/).filter(Boolean);
        const nombreNorm = normalizarTexto(p.nombre);
        return palabras.every(palabra => nombreNorm.includes(palabra));
      }).slice(0, 8)
    : [];

  const seleccionarProveedor = (prov) => {
    setFormData({ 
      ...formData, 
      proveedor_id: prov.id_proveedor, 
      proveedor: prov.nombre,
      categoria_id: prov.categoria_id || ''
    });
    setProveedorBusqueda(prov.nombre);
    setMostrarSugerencias(false);
  };

  const handleProveedorInputChange = (e) => {
    const value = e.target.value;
    setProveedorBusqueda(value);
    setFormData({ ...formData, proveedor: value, proveedor_id: '', categoria_id: '' });
    setMostrarSugerencias(true);
  };

  const abrirModalNuevoProveedor = () => {
    setNuevoProveedorNombre(proveedorBusqueda);
    setNuevoProveedorCategoria('');
    setMostrarSugerencias(false);
    setMostrarModalProveedor(true);
  };

  const crearNuevoProveedor = async () => {
    if (!nuevoProveedorNombre.trim()) {
      toast.error('Ingrese el nombre del proveedor');
      return;
    }
    if (!nuevoProveedorCategoria) {
      toast.error('Seleccione una categoría para el proveedor');
      return;
    }

    try {
      const res = await axios.post(`${API}/proveedores`, {
        nombre: nuevoProveedorNombre.trim(),
        categoria_id: nuevoProveedorCategoria
      });
      
      const nuevoProveedor = res.data;
      toast.success('Proveedor creado exitosamente');
      
      // Agregar a la lista local ordenado alfabéticamente
      setProveedores(prev => [...prev, nuevoProveedor].sort((a, b) =>
        (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' })
      ));
      
      // Seleccionar el nuevo proveedor
      setFormData({
        ...formData,
        proveedor_id: nuevoProveedor.id_proveedor,
        proveedor: nuevoProveedor.nombre,
        categoria_id: nuevoProveedor.categoria_id
      });
      setProveedorBusqueda(nuevoProveedor.nombre);
      
      setMostrarModalProveedor(false);
      setNuevoProveedorNombre('');
      setNuevoProveedorCategoria('');
    } catch (error) {
      toast.error('Error al crear el proveedor');
    }
  };

  const crearNuevaCategoria = async () => {
    if (!nuevaCategoriaNombre.trim()) {
      toast.error('Ingrese el nombre de la categoría');
      return;
    }

    try {
      const res = await axios.post(`${API}/categorias`, {
        nombre: nuevaCategoriaNombre.trim()
      });
      
      const nuevaCategoria = res.data;
      toast.success('Categoría creada exitosamente');
      
      // Agregar a la lista local ordenada alfabéticamente
      setCategorias(prev => [...prev, nuevaCategoria].sort((a, b) =>
        (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' })
      ));
      
      // Seleccionar la nueva categoría
      setNuevoProveedorCategoria(nuevaCategoria.id_categoria);
      
      setMostrarModalCategoria(false);
      setNuevaCategoriaNombre('');
    } catch (error) {
      toast.error('Error al crear la categoría');
    }
  };

  const guardarGasto = async () => {
    if (!formData.fecha || !formData.proveedor || !formData.monto) {
      toast.error('Complete los campos obligatorios: Fecha, Proveedor y Monto');
      return;
    }

    setGuardando(true);
    try {
      await axios.post(`${API}/gastos`, {
        ...formData,
        propiedad_id: propiedadSeleccionada.id_propiedad,
        monto: parseFloat(formData.monto),
        estado: 'pendiente'
      });

      toast.success('Gasto creado exitosamente');
      setGastoCreado(true);
      
      // Resetear formulario después de 2 segundos
      setTimeout(() => {
        setFormData({
          fecha: new Date().toISOString().split('T')[0],
          monto: '',
          categoria_id: '',
          proveedor_id: '',
          proveedor: '',
          medio_pago_id: '',
          notas: ''
        });
        setProveedorBusqueda('');
        setGastoCreado(false);
      }, 2000);
    } catch (error) {
      toast.error('Error al crear el gasto');
    } finally {
      setGuardando(false);
    }
  };

  const limpiarFormulario = () => {
    setFormData({
      fecha: new Date().toISOString().split('T')[0],
      monto: '',
      categoria_id: '',
      proveedor_id: '',
      proveedor: '',
      medio_pago_id: '',
      notas: ''
    });
    setProveedorBusqueda('');
    setGastoCreado(false);
  };

  // ========== FUNCIONES DE CARTOLA ==========
  
  const procesarCartola = async (file) => {
    if (!file) return;
    
    setProcesandoCartola(true);
    setCartolaData(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await axios.post(`${API}/procesar-cartola`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setCartolaData(res.data);
      
      // Inicializar selección (NINGUNO seleccionado por defecto)
      const seleccion = {};
      res.data.consolidado.forEach(item => {
        seleccion[item.proveedor] = false;
      });
      setGastosCartolaSeleccionados(seleccion);
      
      // Inicializar proveedores y categorías por gasto con búsqueda insensible a tildes/mayúsculas
      const provMap = {};
      res.data.consolidado.forEach(item => {
        const itemNorm = normalizarTexto(item.proveedor);
        const proveedorExistente = proveedoresOrdenados.find(p => {
          const pNorm = normalizarTexto(p.nombre);
          return pNorm === itemNorm || pNorm.includes(itemNorm) || itemNorm.includes(pNorm);
        });
        
        provMap[item.proveedor] = {
          id: proveedorExistente?.id_proveedor || null,
          nombre_editable: proveedorExistente?.nombre || item.proveedor,
          crear_nuevo: !proveedorExistente,
          categoria_id: proveedorExistente?.categoria_id || ''
        };
      });
      setProveedoresCartola(provMap);
      
      toast.success(`Se encontraron ${res.data.consolidado.length} proveedores en la cartola`);
    } catch (error) {
      console.error('Error procesando cartola:', error);
      toast.error(error.response?.data?.detail || 'Error al procesar la cartola');
    } finally {
      setProcesandoCartola(false);
    }
  };

  const toggleSeleccionCartola = (proveedor) => {
    setGastosCartolaSeleccionados(prev => ({
      ...prev,
      [proveedor]: !prev[proveedor]
    }));
  };

  const seleccionarProveedorCartola = (proveedorCartola, proveedorId) => {
    const proveedorExistente = proveedores.find(p => p.id_proveedor === proveedorId);
    
    // Categorías asociadas a este proveedor (desde backend o configuración)
    const idsAsociados = new Set(proveedorExistente?.categorias_ids || []);
    if (proveedorExistente?.categoria_id) idsAsociados.add(proveedorExistente.categoria_id);

    setProveedoresCartola(prev => {
      const prevCatId = prev[proveedorCartola]?.categoria_id;
      // Conservar la categoría si ya era una de las asociadas a este proveedor, o usar su categoría por defecto
      const catValida = (prevCatId && idsAsociados.has(prevCatId))
        ? prevCatId
        : (proveedorExistente?.categoria_id || (idsAsociados.size > 0 ? Array.from(idsAsociados)[0] : ''));

      return {
        ...prev,
        [proveedorCartola]: {
          ...prev[proveedorCartola],
          id: proveedorId,
          nombre_editable: proveedorExistente?.nombre || proveedorCartola,
          crear_nuevo: false,
          categoria_id: catValida
        }
      };
    });
    // Resetear 'ver todas' para este ítem para que filtre automáticamente a las de este proveedor
    setMostrarTodasCategorias(prev => ({ ...prev, [proveedorCartola]: false }));
  };

  const marcarCrearNuevoProveedor = (proveedorCartola) => {
    setProveedoresCartola(prev => ({
      ...prev,
      [proveedorCartola]: {
        ...prev[proveedorCartola],
        id: null,
        nombre_editable: prev[proveedorCartola]?.nombre_editable || proveedorCartola,
        crear_nuevo: true
      }
    }));
    // Proveedor nuevo: ofrecer todas las categorías
    setMostrarTodasCategorias(prev => ({ ...prev, [proveedorCartola]: true }));
  };

  // Obtener solo las categorías asociadas al proveedor del gasto en la cartola
  const obtenerCategoriasItemCartola = (proveedorKey) => {
    if (mostrarTodasCategorias[proveedorKey]) {
      return { categorias: categoriasOrdenadas, esFiltrado: false };
    }
    const info = proveedoresCartola[proveedorKey];
    if (!info || info.crear_nuevo || !info.id) {
      return { categorias: categoriasOrdenadas, esFiltrado: false };
    }
    const prov = proveedores.find(p => p.id_proveedor === info.id);
    if (!prov) {
      return { categorias: categoriasOrdenadas, esFiltrado: false };
    }
    const idsAsociados = new Set(prov.categorias_ids || []);
    if (prov.categoria_id) idsAsociados.add(prov.categoria_id);

    if (idsAsociados.size === 0) {
      return { categorias: categoriasOrdenadas, esFiltrado: false };
    }

    const filtradas = categoriasOrdenadas.filter(cat => idsAsociados.has(cat.id_categoria));
    if (filtradas.length === 0) {
      return { categorias: categoriasOrdenadas, esFiltrado: false };
    }
    return { categorias: filtradas, esFiltrado: filtradas.length < categoriasOrdenadas.length };
  };

  const actualizarCategoriaCartola = (proveedorCartola, categoriaId) => {
    setProveedoresCartola(prev => ({
      ...prev,
      [proveedorCartola]: {
        ...prev[proveedorCartola],
        categoria_id: categoriaId
      }
    }));
  };

  const actualizarNombreProveedorCartola = (proveedorCartola, nuevoNombre) => {
    setProveedoresCartola(prev => ({
      ...prev,
      [proveedorCartola]: {
        ...prev[proveedorCartola],
        nombre_editable: nuevoNombre
      }
    }));
  };

  const crearGastosDesdeCartola = async () => {
    // Obtener medio de pago "Tarjeta de crédito limited"
    const medioTarjeta = mediosPago.find(m => 
      m.nombre.toLowerCase().includes('tarjeta') && 
      m.nombre.toLowerCase().includes('limited')
    ) || mediosPago[0];

    if (!medioTarjeta) {
      toast.error('No se encontró el medio de pago "Tarjeta de crédito"');
      return;
    }

    const gastosACrear = cartolaData.consolidado.filter(
      item => gastosCartolaSeleccionados[item.proveedor]
    );

    if (gastosACrear.length === 0) {
      toast.error('Seleccione al menos un gasto para crear');
      return;
    }

    // Validar que todos los gastos seleccionados tengan categoría
    const sinCategoria = gastosACrear.filter(
      item => !proveedoresCartola[item.proveedor]?.categoria_id
    );
    if (sinCategoria.length > 0) {
      toast.error(`Hay ${sinCategoria.length} gasto(s) sin categoría asignada`);
      return;
    }

    // Validar fecha global
    if (!fechaGlobalCartola) {
      toast.error('Debe seleccionar una fecha para los gastos');
      return;
    }

    setCreandoGastosCartola(true);
    let creados = 0;
    let errores = 0;

    try {
      for (const item of gastosACrear) {
        const proveedorInfo = proveedoresCartola[item.proveedor];
        let proveedorId = proveedorInfo?.id;
        let proveedorNombre = proveedorInfo?.nombre_editable || item.proveedor;

        // Crear proveedor si es necesario
        if (proveedorInfo?.crear_nuevo && !proveedorId) {
          try {
            const resProveedor = await axios.post(`${API}/proveedores`, {
              nombre: proveedorNombre,
              categoria_id: proveedorInfo.categoria_id
            });
            proveedorId = resProveedor.data.id_proveedor;
            // Actualizar lista de proveedores ordenado alfabéticamente
            setProveedores(prev => [...prev, resProveedor.data].sort((a, b) =>
              (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' })
            ));
          } catch (e) {
            console.error('Error creando proveedor:', e);
          }
        }

        // Crear gasto con fecha global y categoría individual
        try {
          await axios.post(`${API}/gastos`, {
            propiedad_id: propiedadSeleccionada.id_propiedad,
            fecha: fechaGlobalCartola,
            categoria_id: proveedorInfo.categoria_id,
            proveedor: proveedorNombre,
            proveedor_id: proveedorId,
            medio_pago_id: medioTarjeta.id_medio_pago,
            monto: item.monto_total,
            notas: `Cartola TC - ${item.cantidad} transacción(es)`,
            estado: 'PAGADO'
          });
          creados++;
        } catch (e) {
          console.error('Error creando gasto:', e);
          errores++;
        }
      }

      if (creados > 0) {
        toast.success(`Se crearon ${creados} gastos exitosamente`);
      }
      if (errores > 0) {
        toast.error(`${errores} gastos no pudieron ser creados`);
      }

      // Limpiar y cerrar
      setMostrarModalCartola(false);
      setCartolaData(null);
      setGastosCartolaSeleccionados({});
      setCategoriaCartola('');
      setFechaGlobalCartola(new Date().toISOString().split('T')[0]);
      if (fileInputRef.current) fileInputRef.current.value = '';

    } catch (error) {
      toast.error('Error al crear los gastos');
    } finally {
      setCreandoGastosCartola(false);
    }
  };

  const eliminarGastoCartola = (proveedor) => {
    setCartolaData(prev => ({
      ...prev,
      consolidado: prev.consolidado.filter(item => item.proveedor !== proveedor),
      total: prev.consolidado
        .filter(item => item.proveedor !== proveedor)
        .reduce((sum, item) => sum + item.monto_total, 0)
    }));
    setGastosCartolaSeleccionados(prev => {
      const newState = { ...prev };
      delete newState[proveedor];
      return newState;
    });
  };

  // ========== FUNCIONES DE MOVIMIENTOS PEGADOS (BANCO / NO FACTURADOS) ==========

  const abrirModalMovimientos = () => {
    setMostrarModalMovimientos(true);
    setPropiedadIdMovimientos(propiedadSeleccionada?.id_propiedad || (propiedades[0]?.id_propiedad || ''));
    const medioTarjeta = mediosPago.find(m => 
      m.nombre.toLowerCase().includes('tarjeta') && 
      m.nombre.toLowerCase().includes('limited')
    ) || mediosPago.find(m => m.nombre.toLowerCase().includes('tarjeta')) || mediosPago[0];
    if (medioTarjeta) {
      setMedioPagoGlobalMovimientos(medioTarjeta.id_medio_pago);
    }
  };

  const interpretarMovimientos = async () => {
    if (!textoMovimientos || !textoMovimientos.trim()) {
      toast.error('Pegue primero el texto de los movimientos del banco');
      return;
    }

    setProcesandoMovimientos(true);
    try {
      let movsResultado = [];
      const propId = propiedadIdMovimientos || propiedadSeleccionada?.id_propiedad;

      // 1. Intentar llamar al backend para parseo y detección de duplicados con base de datos
      try {
        const res = await axios.post(`${API}/procesar-movimientos`, {
          texto: textoMovimientos,
          propiedad_id: propId,
          default_year: new Date().getFullYear()
        });
        if (res.data?.movimientos && res.data.movimientos.length > 0) {
          movsResultado = res.data.movimientos.map(m => ({
            ...m,
            medio_pago_id: m.medio_pago_id || medioPagoGlobalMovimientos || ''
          }));
        }
      } catch (backendErr) {
        console.warn('Backend /procesar-movimientos falló o no disponible, usando intérprete local:', backendErr);
        // Fallback local en frontend
        movsResultado = await procesarMovimientosLocal(textoMovimientos, propId);
      }

      if (!movsResultado || movsResultado.length === 0) {
        toast.error('No se detectaron transacciones en el texto pegado');
        return;
      }

      setMovimientosData(movsResultado);
      const cantDuplicados = movsResultado.filter(m => m.es_duplicado).length;
      const cantListos = movsResultado.filter(m => m.seleccionado).length;

      if (cantDuplicados > 0) {
        toast.warning(`Se detectaron ${movsResultado.length} movimientos (${cantDuplicados} posibles duplicados identificados y desmarcados)`);
      } else {
        toast.success(`Se interpretaron ${movsResultado.length} movimientos listos para ingresar`);
      }
    } catch (err) {
      console.error('Error al procesar movimientos:', err);
      toast.error(err.response?.data?.detail || 'Error al procesar movimientos');
    } finally {
      setProcesandoMovimientos(false);
    }
  };

  const procesarMovimientosLocal = async (texto, propId) => {
    const reFechaComp = /\b(\d{1,2})[/-](\d{1,2})[/-](\d{2,4})\b/g;
    const reCuota = /^\s*0?\d{1,2}\s*\/\s*0?\d{1,2}\s*$/;
    const reMonto = /-?\$?\s?\d{1,3}(?:\.\d{3})+(?:,\d{1,2})?|-?\$?\s?\d{4,}(?:,\d{1,2})?/g;
    const skipKws = ['TOTAL', 'SUBTOTAL', 'SALDO', 'RESUMEN', 'CUPO', 'PAGO MINIMO', 'MONTO FACTURADO', 'DISPONIBLE'];

    const lineas = texto.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    const movs = [];
    const anioActual = new Date().getFullYear();

    let fechaActual = null;
    let descAcumulada = [];

    for (const linea of lineas) {
      const upper = linea.toUpperCase();
      if (skipKws.some(kw => upper === kw || upper.startsWith(kw + ':'))) continue;
      if (reCuota.test(linea)) continue;

      const fechas = [...linea.matchAll(reFechaComp)];
      // Si la línea tiene fecha, actualizar fechaActual (fecha superior)
      if (fechas.length > 0) {
        const fMatch = fechas[0];
        const dia = parseInt(fMatch[1], 10);
        const mes = parseInt(fMatch[2], 10);
        let anio = fMatch[3] ? parseInt(fMatch[3], 10) : anioActual;
        if (anio < 100) anio += 2000;
        fechaActual = `${anio}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
      } else {
        // Chequear si es fecha corta al inicio (DD/MM) que no sea cuota
        const mInicio = linea.match(/^\s*(\d{1,2})[/-](\d{1,2})\b/);
        if (mInicio && !reCuota.test(linea)) {
          const dia = parseInt(mInicio[1], 10);
          const mes = parseInt(mInicio[2], 10);
          if (dia >= 1 && dia <= 31 && mes >= 1 && mes <= 12) {
            fechaActual = `${anioActual}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
          }
        }
      }

      const montos = [...linea.matchAll(reMonto)];

      // Caso 1: La línea tiene monto y tenemos una fecha (sea de esta línea o heredada de la superior)
      if (montos.length > 0 && fechaActual) {
        const mLast = montos[montos.length - 1];
        let sMonto = mLast[0].replace(/\$/g, '').replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
        const montoNum = Math.abs(parseFloat(sMonto) || 0);

        if (montoNum > 0) {
          let startDesc = 0;
          if (fechas.length > 0) {
            startDesc = fechas[0].index + fechas[0][0].length;
          }
          let endDesc = mLast.index;
          let rawDesc = (startDesc < endDesc) ? linea.substring(startDesc, endDesc) : linea;
          let limpiaDesc = rawDesc
            .replace(/\b\d{1,2}\/\d{1,2}\b/g, '')
            .replace(/COMPRA NACIONAL|COMPRA INTERNACIONAL|COMPRA NAC\.|COMPRA INT\.|COMPRA EN LINEA|COMPRA ELECTRONICA|COMPRA POS|COMPRA|CARGO|DEBITO|DÉBITO|PAGO SERVICIOS|PAGO EN LINEA|PAGO/gi, '')
            .replace(/\s+/g, ' ')
            .trim();

          const partesDesc = [];
          if (descAcumulada.length > 0) partesDesc.push(...descAcumulada);
          if (limpiaDesc) partesDesc.push(limpiaDesc);

          const descFinal = partesDesc.join(' ').replace(/\s+/g, ' ').trim() || 'Gasto banco';

          movs.push({
            id_temp: 'mov_' + Math.random().toString(36).substr(2, 9),
            fecha: fechaActual,
            descripcion_raw: rawDesc.trim() || linea,
            descripcion: descFinal,
            monto: montoNum,
            es_negativo: false
          });
        }
        descAcumulada = [];
        continue;
      }

      // Caso 2: Solo fecha
      if (fechas.length > 0 && linea.length <= 15) {
        descAcumulada = [];
        continue;
      }

      // Caso 3: Texto de descripción acumulada (entre fechas o antes de un monto)
      if (fechaActual && !reCuota.test(linea)) {
        const limpia = linea.replace(/\b\d{1,2}\/\d{1,2}\b/g, '').trim();
        if (limpia && limpia.length > 1) {
          descAcumulada.push(limpia);
        }
      }
    }

    // Obtener gastos de la propiedad para duplicados
    let gastosDb = [];
    try {
      const resG = await axios.get(`${API}/gastos?propiedad_id=${propId}`);
      gastosDb = resG.data || [];
    } catch (e) {
      console.warn('No se pudieron obtener gastos para duplicados:', e);
    }

    const gastosUsados = new Set();
    return movs.map(m => {
      const descNorm = normalizarTexto(m.descripcion);
      const provExistente = proveedoresOrdenados.find(p => {
        const pNorm = normalizarTexto(p.nombre);
        return pNorm === descNorm || pNorm.includes(descNorm) || descNorm.includes(pNorm);
      });

      const provId = provExistente?.id_proveedor || null;
      const provNombre = provExistente?.nombre || m.descripcion;
      const catId = provExistente?.categoria_id || '';
      const crearNuevo = !provExistente;

      let esDuplicado = false;
      let motivoDuplicado = null;
      let gastoExistente = null;

      for (const g of gastosDb) {
        if (gastosUsados.has(g.id_gasto)) continue;
        if (Math.abs((g.monto || 0) - m.monto) < 1) {
          const coincideFecha = (g.fecha === m.fecha);
          let coincideCercana = false;
          if (!coincideFecha && g.fecha && m.fecha) {
            const diffDias = Math.abs((new Date(g.fecha) - new Date(m.fecha)) / (1000 * 60 * 60 * 24));
            if (diffDias <= 4) coincideCercana = true;
          }
          const gProvNorm = normalizarTexto(g.proveedor);
          const coincideProv = (descNorm && gProvNorm.includes(descNorm)) || (gProvNorm && descNorm.includes(gProvNorm));

          if (coincideFecha || (coincideCercana && coincideProv)) {
            esDuplicado = true;
            gastosUsados.add(g.id_gasto);
            motivoDuplicado = `Gasto registrado: ${formatCurrency(g.monto)} el ${g.fecha} con ${g.proveedor}`;
            gastoExistente = g;
            break;
          }
        }
      }

      return {
        ...m,
        proveedor_id: provId,
        proveedor_nombre: provNombre,
        crear_nuevo_proveedor: crearNuevo,
        categoria_id: catId,
        medio_pago_id: medioPagoGlobalMovimientos || '',
        es_duplicado: esDuplicado,
        motivo_duplicado: motivoDuplicado,
        gasto_existente: gastoExistente,
        seleccionado: !esDuplicado
      };
    });
  };

  const toggleSeleccionMovimiento = (idTemp) => {
    setMovimientosData(prev => prev.map(m => 
      m.id_temp === idTemp ? { ...m, seleccionado: !m.seleccionado } : m
    ));
  };

  const seleccionarTodosMovimientos = (seleccionar) => {
    setMovimientosData(prev => prev.map(m => ({ ...m, seleccionado: seleccionar })));
  };

  const descartarDuplicadosMovimientos = () => {
    const cantDuplicados = movimientosData.filter(m => m.es_duplicado).length;
    setMovimientosData(prev => prev.map(m => 
      m.es_duplicado ? { ...m, seleccionado: false } : m
    ));
    toast.success(`Se desmarcaron ${cantDuplicados} gastos duplicados`);
  };

  const eliminarMovimiento = (idTemp) => {
    setMovimientosData(prev => prev.filter(m => m.id_temp !== idTemp));
  };

  const cambiarOrden = (campo) => {
    if (ordenCampo === campo) {
      setOrdenDireccion(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrdenCampo(campo);
      setOrdenDireccion('desc');
    }
  };

  const movimientosFiltradosYOrdenados = useMemo(() => {
    const list = movimientosData.filter(m => {
      if (filtroMovimientos === 'listos') return !m.es_duplicado;
      if (filtroMovimientos === 'duplicados') return m.es_duplicado;
      return true;
    });

    return [...list].sort((a, b) => {
      if (ordenCampo === 'fecha') {
        const comp = (a.fecha || '').localeCompare(b.fecha || '');
        return ordenDireccion === 'asc' ? comp : -comp;
      } else if (ordenCampo === 'monto') {
        const valA = Number(a.monto) || 0;
        const valB = Number(b.monto) || 0;
        return ordenDireccion === 'asc' ? valA - valB : valB - valA;
      }
      return 0;
    });
  }, [movimientosData, filtroMovimientos, ordenCampo, ordenDireccion]);

  const actualizarFechaMovimiento = (idTemp, fecha) => {
    setMovimientosData(prev => prev.map(m => 
      m.id_temp === idTemp ? { ...m, fecha } : m
    ));
  };

  const actualizarMontoMovimiento = (idTemp, monto) => {
    setMovimientosData(prev => prev.map(m => 
      m.id_temp === idTemp ? { ...m, monto: parseFloat(monto) || 0 } : m
    ));
  };

  const actualizarNombreProveedorMovimiento = (idTemp, nuevoNombre) => {
    setMovimientosData(prev => prev.map(m => 
      m.id_temp === idTemp ? { ...m, proveedor_nombre: nuevoNombre } : m
    ));
  };

  const seleccionarProveedorMovimiento = (idTemp, provId) => {
    const prov = proveedores.find(p => p.id_proveedor === provId);
    const idsAsociados = new Set(prov?.categorias_ids || []);
    if (prov?.categoria_id) idsAsociados.add(prov.categoria_id);

    setMovimientosData(prev => prev.map(m => {
      if (m.id_temp !== idTemp) return m;
      const prevCatId = m.categoria_id;
      const catValida = (prevCatId && idsAsociados.has(prevCatId))
        ? prevCatId
        : (prov?.categoria_id || (idsAsociados.size > 0 ? Array.from(idsAsociados)[0] : ''));

      return {
        ...m,
        proveedor_id: provId,
        proveedor_nombre: prov?.nombre || m.proveedor_nombre,
        crear_nuevo_proveedor: false,
        categoria_id: catValida
      };
    }));
    setMostrarTodasCategoriasMov(prev => ({ ...prev, [idTemp]: false }));
  };

  const marcarCrearNuevoProveedorMovimiento = (idTemp) => {
    setMovimientosData(prev => prev.map(m => 
      m.id_temp === idTemp ? { ...m, proveedor_id: null, crear_nuevo_proveedor: true } : m
    ));
    setMostrarTodasCategoriasMov(prev => ({ ...prev, [idTemp]: true }));
  };

  const actualizarCategoriaMovimiento = (idTemp, categoriaId) => {
    setMovimientosData(prev => prev.map(m => 
      m.id_temp === idTemp ? { ...m, categoria_id: categoriaId } : m
    ));
  };

  const actualizarMedioPagoMovimiento = (idTemp, medioId) => {
    setMovimientosData(prev => prev.map(m => 
      m.id_temp === idTemp ? { ...m, medio_pago_id: medioId } : m
    ));
  };

  const obtenerCategoriasItemMovimiento = (item) => {
    if (mostrarTodasCategoriasMov[item.id_temp]) {
      return { categorias: categoriasOrdenadas, esFiltrado: false };
    }
    if (item.crear_nuevo_proveedor || !item.proveedor_id) {
      return { categorias: categoriasOrdenadas, esFiltrado: false };
    }
    const prov = proveedores.find(p => p.id_proveedor === item.proveedor_id);
    if (!prov) {
      return { categorias: categoriasOrdenadas, esFiltrado: false };
    }
    const idsAsociados = new Set(prov.categorias_ids || []);
    if (prov.categoria_id) idsAsociados.add(prov.categoria_id);
    if (idsAsociados.size === 0) {
      return { categorias: categoriasOrdenadas, esFiltrado: false };
    }
    const filtradas = categoriasOrdenadas.filter(cat => idsAsociados.has(cat.id_categoria));
    if (filtradas.length === 0) {
      return { categorias: categoriasOrdenadas, esFiltrado: false };
    }
    return { categorias: filtradas, esFiltrado: filtradas.length < categoriasOrdenadas.length };
  };

  const crearGastosDesdeMovimientos = async () => {
    const propId = propiedadIdMovimientos || propiedadSeleccionada?.id_propiedad;
    if (!propId) {
      toast.error('Debe seleccionar una propiedad');
      return;
    }

    const seleccionados = movimientosData.filter(m => m.seleccionado);
    if (seleccionados.length === 0) {
      toast.error('Seleccione al menos un gasto para crear');
      return;
    }

    const sinCategoria = seleccionados.filter(m => !m.categoria_id);
    if (sinCategoria.length > 0) {
      toast.error(`Hay ${sinCategoria.length} gasto(s) sin categoría asignada`);
      return;
    }

    const sinFecha = seleccionados.filter(m => !m.fecha);
    if (sinFecha.length > 0) {
      toast.error(`Hay ${sinFecha.length} gasto(s) sin fecha válida`);
      return;
    }

    setCreandoGastosMovimientos(true);
    let creados = 0;
    let provsCreados = 0;
    let errores = 0;

    try {
      for (const item of seleccionados) {
        let provId = item.proveedor_id;
        let provNombre = item.proveedor_nombre || item.descripcion;

        if (item.crear_nuevo_proveedor && !provId) {
          try {
            const resProv = await axios.post(`${API}/proveedores`, {
              nombre: provNombre,
              categoria_id: item.categoria_id
            });
            provId = resProv.data.id_proveedor;
            provsCreados++;
            setProveedores(prev => [...prev, resProv.data].sort((a, b) =>
              (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' })
            ));
          } catch (e) {
            console.error('Error creando proveedor:', e);
          }
        }

        const medioPagoId = item.medio_pago_id || medioPagoGlobalMovimientos || mediosPago[0]?.id_medio_pago;

        try {
          await axios.post(`${API}/gastos`, {
            propiedad_id: propId,
            fecha: item.fecha,
            categoria_id: item.categoria_id,
            proveedor: provNombre,
            proveedor_id: provId,
            medio_pago_id: medioPagoId,
            monto: item.monto,
            notas: `Movimientos Banco - ${item.descripcion_raw || item.descripcion}`,
            estado: 'PAGADO'
          });
          creados++;
        } catch (e) {
          console.error('Error creando gasto:', e);
          errores++;
        }
      }

      if (creados > 0) {
        toast.success(`Se crearon ${creados} gastos exitosamente ${provsCreados > 0 ? `(${provsCreados} proveedores nuevos creados)` : ''}`);
      }
      if (errores > 0) {
        toast.error(`${errores} gastos no pudieron ser creados`);
      }

      setMostrarModalMovimientos(false);
      setTextoMovimientos('');
      setMovimientosData([]);
      setMostrarTodasCategoriasMov({});
      setFiltroMovimientos('todos');

    } catch (error) {
      toast.error('Error al registrar los gastos');
    } finally {
      setCreandoGastosMovimientos(false);
    }
  };

  // Formatear moneda
  const formatCurrency = (value) => {
    return `$${Math.round(value || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;
  };

  // Calcular diferencia de monto facturado
  const calcularDiferencia = async () => {
    if (!formFacturado.monto || !formFacturado.fecha || !formFacturado.medio_pago_id) {
      toast.error('Complete todos los campos: Monto, Fecha y Medio de Pago');
      return;
    }

    setCalculando(true);
    try {
      // Obtener gastos de la propiedad
      const res = await axios.get(`${API}/gastos?propiedad_id=${propiedadSeleccionada.id_propiedad}`);
      const todosGastos = res.data;
      
      // Extraer año y mes de la fecha seleccionada
      const fechaSeleccionada = new Date(formFacturado.fecha);
      const mesSeleccionado = fechaSeleccionada.getMonth();
      const anioSeleccionado = fechaSeleccionada.getFullYear();
      
      // Filtrar gastos por medio de pago y mes
      const gastosFiltrados = todosGastos.filter(g => {
        const fechaGasto = new Date(g.fecha);
        return g.medio_pago_id === formFacturado.medio_pago_id &&
               fechaGasto.getMonth() === mesSeleccionado &&
               fechaGasto.getFullYear() === anioSeleccionado;
      });
      
      // Sumar todos los gastos del mes con ese medio de pago
      const totalGastos = gastosFiltrados.reduce((sum, g) => sum + (g.monto || 0), 0);
      
      // Calcular diferencia: Monto ingresado - Total gastos
      const montoIngresado = parseFloat(formFacturado.monto);
      const diferencia = montoIngresado - totalGastos;
      
      setGastosDelMes(gastosFiltrados);
      setResultadoCalculo({
        montoIngresado,
        totalGastos,
        diferencia,
        cantidadGastos: gastosFiltrados.length,
        mes: fechaSeleccionada.toLocaleString('es-ES', { month: 'long', year: 'numeric' })
      });
      
    } catch (error) {
      toast.error('Error al calcular');
    } finally {
      setCalculando(false);
    }
  };

  // Agregar el gasto de diferencia
  const agregarGastoDiferencia = async () => {
    if (!resultadoCalculo) return;
    
    setAgregandoDiferencia(true);
    try {
      // Buscar o crear proveedor "TC WM"
      let proveedorTCWM = proveedores.find(p => p.nombre.toUpperCase() === 'TC WM');
      if (!proveedorTCWM) {
        // Obtener primera categoría disponible para el proveedor
        const categoriaDefault = categorias[0];
        if (!categoriaDefault) {
          toast.error('No hay categorías disponibles para crear el proveedor');
          return;
        }
        const resProveedor = await axios.post(`${API}/proveedores`, {
          nombre: 'TC WM',
          categoria_id: categoriaDefault.id_categoria
        });
        proveedorTCWM = resProveedor.data;
        setProveedores(prev => [...prev, proveedorTCWM].sort((a, b) =>
          (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' })
        ));
      }
      
      // Buscar o crear medio de pago "Diferencia facturado"
      let medioDiferencia = mediosPago.find(m => m.nombre.toLowerCase() === 'diferencia facturado');
      if (!medioDiferencia) {
        const resMedio = await axios.post(`${API}/medios-pago`, {
          nombre: 'Diferencia facturado',
          activo: true
        });
        medioDiferencia = resMedio.data;
        setMediosPago([...mediosPago, medioDiferencia]);
      }
      
      // Crear el gasto con estado PAGADO
      await axios.post(`${API}/gastos`, {
        propiedad_id: propiedadSeleccionada.id_propiedad,
        fecha: formFacturado.fecha,
        monto: Math.abs(resultadoCalculo.diferencia),
        proveedor: proveedorTCWM.nombre,
        proveedor_id: proveedorTCWM.id_proveedor,
        categoria_id: proveedorTCWM.categoria_id,
        medio_pago_id: medioDiferencia.id_medio_pago,
        notas: `Diferencia facturado ${resultadoCalculo.mes} - ${mediosPago.find(m => m.id_medio_pago === formFacturado.medio_pago_id)?.nombre || ''}`,
        estado: 'PAGADO'
      });
      
      toast.success('Gasto de diferencia creado exitosamente');
      
      // Limpiar el formulario de facturado
      setFormFacturado({
        monto: '',
        fecha: new Date().toISOString().split('T')[0],
        medio_pago_id: ''
      });
      setResultadoCalculo(null);
      setGastosDelMes([]);
      setMostrarCalculadora(false);
      
    } catch (error) {
      toast.error('Error al crear el gasto de diferencia');
    } finally {
      setAgregandoDiferencia(false);
    }
  };

  // Vista de selección de propiedad
  if (!propiedadSeleccionada) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 p-4">
        <Toaster position="top-right" />
        <div className="max-w-md mx-auto pt-10">
          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mb-4">
                <Building2 className="w-8 h-8 text-cyan-600" />
              </div>
              <CardTitle className="text-2xl text-cyan-800">Nuevo Gasto Rápido</CardTitle>
              <p className="text-gray-500 mt-2">Seleccione la propiedad donde registrar el gasto</p>
            </CardHeader>
            <CardContent>
              {cargandoPropiedades ? (
                <div className="text-center py-8 text-gray-500">Cargando propiedades...</div>
              ) : propiedades.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No hay propiedades disponibles</div>
              ) : (
                <div className="space-y-3">
                  {propiedades.map(prop => (
                    <button
                      key={prop.id_propiedad}
                      onClick={() => setPropiedadSeleccionada(prop)}
                      className="w-full p-4 text-left border rounded-lg hover:bg-cyan-50 hover:border-cyan-300 transition-colors"
                    >
                      <div className="font-medium text-gray-800">{prop.nombre}</div>
                      {prop.direccion && (
                        <div className="text-sm text-gray-500">{prop.direccion}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Vista del formulario de gasto
  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50 p-4">
      <Toaster position="top-right" />
      <div className="max-w-lg mx-auto pt-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-xl text-cyan-800 flex items-center gap-2">
                  <Receipt className="w-5 h-5" />
                  Nuevo Gasto
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">{propiedadSeleccionada.nombre}</p>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setPropiedadSeleccionada(null)}
                className="text-gray-500"
              >
                Cambiar
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {gastoCreado ? (
              <div className="text-center py-10">
                <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-semibold text-green-800">¡Gasto Registrado!</h3>
                <p className="text-gray-500 mt-2">El formulario se limpiará automáticamente</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Fecha y Monto */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-gray-700">Fecha *</Label>
                    <Input
                      type="date"
                      value={formData.fecha}
                      onChange={(e) => setFormData({ ...formData, fecha: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label className="text-gray-700">Monto *</Label>
                    <Input
                      type="number"
                      value={formData.monto}
                      onChange={(e) => setFormData({ ...formData, monto: e.target.value })}
                      placeholder="$0"
                      className="text-right"
                    />
                  </div>
                </div>

                {/* Proveedor con autocompletado */}
                <div className="relative">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-gray-700">Proveedor *</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={abrirModalNuevoProveedor}
                      className="text-cyan-600 text-xs h-auto py-0"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Nuevo
                    </Button>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      ref={inputRef}
                      type="text"
                      value={proveedorBusqueda}
                      onChange={handleProveedorInputChange}
                      onFocus={() => setMostrarSugerencias(true)}
                      placeholder="Escriba para buscar..."
                      className="pl-9 pr-9"
                    />
                    {proveedorBusqueda && (
                      <button
                        type="button"
                        onClick={() => {
                          setProveedorBusqueda('');
                          setFormData({ ...formData, proveedor: '', proveedor_id: '', categoria_id: '' });
                        }}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  
                  {mostrarSugerencias && (proveedoresSugeridos.length > 0 || proveedorBusqueda.trim()) && (
                    <div 
                      ref={sugerenciasRef}
                      className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-56 overflow-auto"
                    >
                      {proveedoresSugeridos.map(prov => (
                        <button
                          key={prov.id_proveedor}
                          type="button"
                          onClick={() => seleccionarProveedor(prov)}
                          className="w-full px-3 py-2 text-left hover:bg-cyan-50 text-sm flex justify-between items-center border-b border-gray-100 last:border-0"
                        >
                          <span className="font-medium">{prov.nombre}</span>
                          <span className="text-xs text-gray-400">
                            {categorias.find(c => c.id_categoria === prov.categoria_id)?.nombre}
                          </span>
                        </button>
                      ))}
                      {proveedorBusqueda.trim() && (
                        <button
                          type="button"
                          onClick={abrirModalNuevoProveedor}
                          className="w-full px-3 py-2 text-left hover:bg-green-50 text-sm flex items-center gap-2 text-green-600 bg-green-50/50"
                        >
                          <Plus className="w-4 h-4" />
                          <span>Crear &quot;{proveedorBusqueda.trim()}&quot;</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Medio de Pago */}
                <div>
                  <Label className="text-gray-700">Medio de Pago</Label>
                  <Select 
                    value={formData.medio_pago_id || '__ninguno__'} 
                    onValueChange={(value) => setFormData({ ...formData, medio_pago_id: value === '__ninguno__' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__ninguno__">-- Sin especificar --</SelectItem>
                      {mediosPago.map(medio => (
                        <SelectItem key={medio.id_medio_pago} value={medio.id_medio_pago}>
                          {medio.nombre}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Notas */}
                <div>
                  <Label className="text-gray-700">Notas</Label>
                  <Input
                    value={formData.notas}
                    onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                    placeholder="Descripción del gasto"
                  />
                </div>

                {/* Botones */}
                <div className="flex gap-3 pt-4">
                  <Button 
                    variant="outline" 
                    onClick={limpiarFormulario}
                    className="flex-1"
                  >
                    Limpiar
                  </Button>
                  <Button 
                    onClick={guardarGasto}
                    disabled={guardando || !formData.fecha || !formData.proveedor || !formData.monto}
                    className="flex-1 bg-cyan-600 hover:bg-cyan-700"
                  >
                    {guardando ? 'Guardando...' : 'Guardar Gasto'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Link para volver */}
        <div className="text-center mt-4">
          <a href="/" className="text-sm text-cyan-600 hover:underline">
            Ir al panel principal
          </a>
        </div>

        {/* Botón Ingreso Monto Facturado */}
        <div className="mt-6 space-y-3">
          <Button 
            onClick={() => setMostrarCalculadora(true)}
            variant="outline"
            className="w-full border-orange-400 text-orange-600 hover:bg-orange-50"
            data-testid="btn-ingreso-facturado"
          >
            <Calculator className="w-5 h-5 mr-2" />
            Ingreso Monto Facturado
          </Button>
          
          <Button 
            onClick={() => setMostrarModalCartola(true)}
            variant="outline"
            className="w-full border-purple-400 text-purple-600 hover:bg-purple-50"
            data-testid="btn-procesar-cartola"
          >
            <CreditCard className="w-5 h-5 mr-2" />
            Procesar Cartola Tarjeta Crédito (PDF)
          </Button>

          <Button 
            onClick={abrirModalMovimientos}
            variant="outline"
            className="w-full border-indigo-400 text-indigo-700 hover:bg-indigo-50"
            data-testid="btn-procesar-movimientos"
          >
            <FileText className="w-5 h-5 mr-2 text-indigo-600" />
            Pegar Movimientos por Facturar (Banco)
          </Button>
        </div>
      </div>

      {/* Modal: Nuevo Proveedor */}
      <Dialog open={mostrarModalProveedor} onOpenChange={setMostrarModalProveedor}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-cyan-800">
              Nuevo Proveedor
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-700">Nombre del Proveedor *</Label>
              <Input
                value={nuevoProveedorNombre}
                onChange={(e) => setNuevoProveedorNombre(e.target.value)}
                placeholder="Ej: Empresa ABC"
              />
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-gray-700">Categoría *</Label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setMostrarModalCategoria(true)}
                  className="text-cyan-600 text-xs h-auto py-0"
                >
                  <Plus className="w-3 h-3 mr-1" />
                  Nueva
                </Button>
              </div>
              <Select 
                value={nuevoProveedorCategoria} 
                onValueChange={setNuevoProveedorCategoria}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar categoría" />
                </SelectTrigger>
                <SelectContent>
                  {categoriasOrdenadas.map(cat => (
                    <SelectItem key={cat.id_categoria} value={cat.id_categoria}>
                      {cat.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setMostrarModalProveedor(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={crearNuevoProveedor}
              className="bg-cyan-600 hover:bg-cyan-700"
              disabled={!nuevoProveedorNombre.trim() || !nuevoProveedorCategoria}
            >
              Crear Proveedor
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Nueva Categoría */}
      <Dialog open={mostrarModalCategoria} onOpenChange={setMostrarModalCategoria}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-cyan-800">
              Nueva Categoría
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-700">Nombre de la Categoría *</Label>
              <Input
                value={nuevaCategoriaNombre}
                onChange={(e) => setNuevaCategoriaNombre(e.target.value)}
                placeholder="Ej: Servicios básicos"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setMostrarModalCategoria(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={crearNuevaCategoria}
              className="bg-cyan-600 hover:bg-cyan-700"
              disabled={!nuevaCategoriaNombre.trim()}
            >
              Crear Categoría
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Calculadora de Monto Facturado */}
      <Dialog open={mostrarCalculadora} onOpenChange={(open) => {
        setMostrarCalculadora(open);
        if (!open) {
          setResultadoCalculo(null);
          setGastosDelMes([]);
        }
      }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-orange-700 flex items-center gap-2">
              <Calculator className="w-5 h-5" />
              Ingreso Monto Facturado
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Formulario de entrada */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-700">Monto Facturado *</Label>
                <Input
                  type="number"
                  value={formFacturado.monto}
                  onChange={(e) => setFormFacturado({ ...formFacturado, monto: e.target.value })}
                  placeholder="$0"
                  className="text-right"
                  data-testid="input-monto-facturado"
                />
              </div>
              <div>
                <Label className="text-gray-700">Fecha *</Label>
                <Input
                  type="date"
                  value={formFacturado.fecha}
                  onChange={(e) => setFormFacturado({ ...formFacturado, fecha: e.target.value })}
                  data-testid="input-fecha-facturado"
                />
              </div>
            </div>

            <div>
              <Label className="text-gray-700">Medio de Pago *</Label>
              <Select 
                value={formFacturado.medio_pago_id || ''} 
                onValueChange={(value) => setFormFacturado({ ...formFacturado, medio_pago_id: value })}
              >
                <SelectTrigger data-testid="select-medio-pago-facturado">
                  <SelectValue placeholder="Seleccionar medio de pago" />
                </SelectTrigger>
                <SelectContent>
                  {mediosPago.map(medio => (
                    <SelectItem key={medio.id_medio_pago} value={medio.id_medio_pago}>
                      {medio.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Botón Calcular */}
            <Button 
              onClick={calcularDiferencia}
              disabled={calculando || !formFacturado.monto || !formFacturado.fecha || !formFacturado.medio_pago_id}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold"
              data-testid="btn-calcular"
            >
              {calculando ? 'Calculando...' : 'CALCULAR'}
            </Button>

            {/* Resultado del cálculo */}
            {resultadoCalculo && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <h4 className="font-semibold text-gray-700 mb-3">
                  Resultado - {resultadoCalculo.mes}
                </h4>
                
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monto Facturado:</span>
                    <span className="font-medium text-green-600">{formatCurrency(resultadoCalculo.montoIngresado)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Gastos ({resultadoCalculo.cantidadGastos}):</span>
                    <span className="font-medium text-red-600">- {formatCurrency(resultadoCalculo.totalGastos)}</span>
                  </div>
                  <hr className="my-2" />
                  <div className="flex justify-between text-lg">
                    <span className="font-semibold text-gray-800">Diferencia:</span>
                    <span className={`font-bold ${resultadoCalculo.diferencia >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(resultadoCalculo.diferencia)}
                    </span>
                  </div>
                </div>

                {/* Botón Agregar a Gasto */}
                <div className="mt-4 flex justify-end">
                  <Button 
                    onClick={agregarGastoDiferencia}
                    disabled={agregandoDiferencia}
                    className="bg-cyan-600 hover:bg-cyan-700"
                    data-testid="btn-agregar-gasto-diferencia"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    {agregandoDiferencia ? 'Agregando...' : 'Agregar a Gasto'}
                  </Button>
                </div>

                {/* Detalle de gastos del mes (colapsable) */}
                {gastosDelMes.length > 0 && (
                  <details className="mt-4">
                    <summary className="cursor-pointer text-sm text-cyan-600 hover:text-cyan-800">
                      Ver detalle de gastos ({gastosDelMes.length})
                    </summary>
                    <div className="mt-2 max-h-40 overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-2 py-1 text-left">Fecha</th>
                            <th className="px-2 py-1 text-left">Proveedor</th>
                            <th className="px-2 py-1 text-right">Monto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {gastosDelMes.map((g, idx) => (
                            <tr key={idx} className="border-b border-gray-100">
                              <td className="px-2 py-1">{g.fecha}</td>
                              <td className="px-2 py-1">{g.proveedor || '-'}</td>
                              <td className="px-2 py-1 text-right">{formatCurrency(g.monto)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => {
              setMostrarCalculadora(false);
              setResultadoCalculo(null);
              setGastosDelMes([]);
            }}>
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Procesar Cartola de Tarjeta de Crédito */}
      <Dialog open={mostrarModalCartola} onOpenChange={(open) => {
        setMostrarModalCartola(open);
        if (!open) {
          setCartolaData(null);
          setGastosCartolaSeleccionados({});
          setCategoriaCartola('');
          setFechaGlobalCartola(new Date().toISOString().split('T')[0]);
          setMostrarTodasCategorias({});
          if (fileInputRef.current) fileInputRef.current.value = '';
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-purple-800 flex items-center gap-2">
              <CreditCard className="w-6 h-6" />
              Procesar Cartola de Tarjeta de Crédito
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Upload PDF */}
            <div className="border-2 border-dashed border-purple-200 rounded-lg p-6 text-center">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={(e) => procesarCartola(e.target.files[0])}
                className="hidden"
                id="cartola-upload"
              />
              <label 
                htmlFor="cartola-upload"
                className="cursor-pointer block"
              >
                <Upload className="w-12 h-12 mx-auto text-purple-400 mb-3" />
                <p className="text-purple-700 font-medium">
                  {procesandoCartola ? 'Procesando...' : 'Haga clic para cargar la cartola PDF'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Formato: Estado de cuenta Banco Santander
                </p>
              </label>
            </div>

            {procesandoCartola && (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                <p className="mt-4 text-purple-700">Analizando cartola con IA...</p>
                <p className="text-sm text-gray-500">Esto puede tomar unos segundos</p>
              </div>
            )}

            {cartolaData && (
              <>
                {/* Resumen */}
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-sm text-purple-600">Transacciones</p>
                      <p className="text-2xl font-bold text-purple-800">{cartolaData.transacciones.length}</p>
                    </div>
                    <div>
                      <p className="text-sm text-purple-600">Proveedores</p>
                      <p className="text-2xl font-bold text-purple-800">{cartolaData.consolidado.length}</p>
                    </div>
                    <div>
                      <p className="text-sm text-purple-600">Total</p>
                      <p className="text-2xl font-bold text-purple-800">{formatCurrency(cartolaData.total)}</p>
                    </div>
                  </div>
                </div>

                {/* Fecha global para todos los gastos */}
                <div className="bg-purple-100 rounded-lg p-4 flex items-center gap-4">
                  <Label className="text-purple-800 font-medium whitespace-nowrap">
                    Fecha para todos los gastos:
                  </Label>
                  <Input
                    type="date"
                    value={fechaGlobalCartola}
                    onChange={(e) => setFechaGlobalCartola(e.target.value)}
                    className="max-w-[200px] bg-white"
                    data-testid="fecha-global-cartola"
                  />
                </div>

                {/* Lista de gastos consolidados */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-purple-600 text-white p-2 text-xs font-medium grid grid-cols-24 gap-1">
                    <div className="col-span-1 text-center">Sel.</div>
                    <div className="col-span-5">Descripción Cartola</div>
                    <div className="col-span-5">Nombre Proveedor</div>
                    <div className="col-span-4">Asignar a</div>
                    <div className="col-span-4">Categoría</div>
                    <div className="col-span-3 text-right">Total</div>
                    <div className="col-span-2 text-center"></div>
                  </div>
                  <div className="max-h-[350px] overflow-y-auto">
                    {cartolaData.consolidado.map((item, idx) => (
                      <div 
                        key={item.proveedor} 
                        className={`grid grid-cols-24 gap-1 p-2 items-center border-b text-xs ${
                          idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        } ${!gastosCartolaSeleccionados[item.proveedor] ? 'opacity-40' : ''}`}
                      >
                        <div className="col-span-1 text-center">
                          <input
                            type="checkbox"
                            checked={gastosCartolaSeleccionados[item.proveedor] || false}
                            onChange={() => toggleSeleccionCartola(item.proveedor)}
                            className="w-4 h-4 text-purple-600 rounded"
                          />
                        </div>
                        <div className="col-span-5">
                          <p className="font-medium text-gray-600 truncate text-[10px]" title={item.proveedor}>{item.proveedor}</p>
                          <p className="text-gray-400 text-[9px]">{item.cantidad} tx</p>
                        </div>
                        <div className="col-span-5">
                          <input
                            type="text"
                            value={proveedoresCartola[item.proveedor]?.nombre_editable || ''}
                            onChange={(e) => actualizarNombreProveedorCartola(item.proveedor, e.target.value)}
                            className="w-full text-xs border rounded p-1 font-medium"
                            disabled={!gastosCartolaSeleccionados[item.proveedor]}
                            placeholder="Nombre del proveedor"
                            data-testid={`nombre-proveedor-${idx}`}
                          />
                        </div>
                        <div className="col-span-4">
                          <SelectorProveedorCartola
                            valorId={proveedoresCartola[item.proveedor]?.id || 'nuevo'}
                            nombreCartola={item.proveedor}
                            nombreEditable={proveedoresCartola[item.proveedor]?.nombre_editable}
                            proveedores={proveedoresOrdenados}
                            categorias={categoriasOrdenadas}
                            disabled={!gastosCartolaSeleccionados[item.proveedor]}
                            onSelectProveedor={(id) => seleccionarProveedorCartola(item.proveedor, id)}
                            onMarcarNuevo={() => marcarCrearNuevoProveedor(item.proveedor)}
                          />
                        </div>
                        <div className="col-span-4">
                          {(() => {
                            const { categorias: catsItem, esFiltrado } = obtenerCategoriasItemCartola(item.proveedor);
                            return (
                              <select
                                value={proveedoresCartola[item.proveedor]?.categoria_id || ''}
                                onChange={(e) => {
                                  if (e.target.value === '__ver_todas__') {
                                    setMostrarTodasCategorias(prev => ({ ...prev, [item.proveedor]: true }));
                                  } else {
                                    actualizarCategoriaCartola(item.proveedor, e.target.value);
                                  }
                                }}
                                className={`w-full text-xs border rounded p-1 ${
                                  gastosCartolaSeleccionados[item.proveedor] && !proveedoresCartola[item.proveedor]?.categoria_id 
                                    ? 'border-red-300 bg-red-50' 
                                    : ''
                                }`}
                                disabled={!gastosCartolaSeleccionados[item.proveedor]}
                              >
                                <option value="">Categoría...</option>
                                {catsItem.map(cat => (
                                  <option key={cat.id_categoria} value={cat.id_categoria}>
                                    {cat.nombre}
                                  </option>
                                ))}
                                {esFiltrado && (
                                  <option value="__ver_todas__">➕ Ver todas ({categoriasOrdenadas.length})...</option>
                                )}
                              </select>
                            );
                          })()}
                        </div>
                        <div className="col-span-3 text-right font-medium text-purple-700">
                          {formatCurrency(item.monto_total)}
                        </div>
                        <div className="col-span-2 text-center">
                          <button
                            onClick={() => eliminarGastoCartola(item.proveedor)}
                            className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                            title="Eliminar"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Total seleccionado */}
                <div className="flex justify-between items-center bg-gray-100 rounded-lg p-3">
                  <span className="text-gray-600">
                    Total seleccionado ({Object.values(gastosCartolaSeleccionados).filter(Boolean).length} gastos):
                  </span>
                  <span className="text-xl font-bold text-purple-700">
                    {formatCurrency(
                      cartolaData.consolidado
                        .filter(item => gastosCartolaSeleccionados[item.proveedor])
                        .reduce((sum, item) => sum + item.monto_total, 0)
                    )}
                  </span>
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => {
                setMostrarModalCartola(false);
                setCartolaData(null);
              }}
            >
              Cancelar
            </Button>
            {cartolaData && (
              <Button 
                onClick={crearGastosDesdeCartola}
                className="bg-purple-600 hover:bg-purple-700"
                disabled={creandoGastosCartola || Object.values(gastosCartolaSeleccionados).filter(Boolean).length === 0}
              >
                {creandoGastosCartola ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creando...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-2" />
                    Crear Gastos Seleccionados
                  </>
                )}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal: Procesar Movimientos por Facturar (Banco) */}
      <Dialog open={mostrarModalMovimientos} onOpenChange={(open) => {
        setMostrarModalMovimientos(open);
        if (!open) {
          setTextoMovimientos('');
          setMovimientosData([]);
          setMostrarTodasCategoriasMov({});
          setFiltroMovimientos('todos');
        }
      }}>
        <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-indigo-900 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-6 h-6 text-indigo-600" />
                <span>Procesar Movimientos por Facturar (Banco)</span>
              </div>
              {movimientosData.length > 0 && (
                <span className="text-xs font-normal text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
                  {movimientosData.length} movimientos detectados
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Si aún no hemos procesado texto o queremos volver a pegar */}
            {movimientosData.length === 0 ? (
              <div className="space-y-4">
                <div className="bg-indigo-50/70 border border-indigo-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-indigo-600 shrink-0 mt-0.5" />
                    <div className="text-xs text-indigo-950 space-y-1">
                      <p className="font-semibold text-sm text-indigo-900">
                        Copie y pegue aquí la tabla o lista de movimientos de su banco
                      </p>
                      <p className="text-indigo-800">
                        Compatible con movimientos no facturados de <strong>Banco Santander, Banco de Chile, BCI, Scotiabank, Itaú, BancoEstado</strong> y cartolas en texto o Excel.
                      </p>
                      <p className="text-indigo-700/80">
                        El sistema extraerá automáticamente <strong>fechas, proveedores y montos</strong>, identificará si algún gasto <strong>ya fue ingresado a la app</strong> para advertirle y ofrecer descartarlo, y le permitirá asignar o crear proveedores nuevos con un clic.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-medium text-gray-700">Propiedad de destino *</Label>
                    <select
                      value={propiedadIdMovimientos}
                      onChange={(e) => setPropiedadIdMovimientos(e.target.value)}
                      className="w-full text-xs border rounded p-2 mt-1 bg-white focus:ring-1 focus:ring-indigo-500"
                    >
                      {propiedades.map(p => (
                        <option key={p.id_propiedad} value={p.id_propiedad}>
                          {p.nombre} {p.ubicacion ? `(${p.ubicacion})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <Label className="text-xs font-medium text-gray-700">Medio de pago por defecto *</Label>
                    <select
                      value={medioPagoGlobalMovimientos}
                      onChange={(e) => setMedioPagoGlobalMovimientos(e.target.value)}
                      className="w-full text-xs border rounded p-2 mt-1 bg-white focus:ring-1 focus:ring-indigo-500"
                    >
                      {mediosPago.map(m => (
                        <option key={m.id_medio_pago} value={m.id_medio_pago}>
                          {m.nombre}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <Label className="text-xs font-semibold text-gray-800">
                      Pegar texto de movimientos *
                    </Label>
                    {textoMovimientos && (
                      <button
                        type="button"
                        onClick={() => setTextoMovimientos('')}
                        className="text-[11px] text-gray-500 hover:text-red-600 transition-colors"
                      >
                        Limpiar texto
                      </button>
                    )}
                  </div>
                  <textarea
                    rows={9}
                    value={textoMovimientos}
                    onChange={(e) => setTextoMovimientos(e.target.value)}
                    placeholder={`Pegue aquí las filas copiadas desde el portal web del banco...\n\nEjemplo Santander:\n18/10/2026\tUBER *EATS CHILE\tSANTIAGO\t01/01\t$ 15.490\n17/10/2026\tFALABELLA PORTAL\tSANTIAGO\t01/03\t$ 45.990\n15/10/2026\tCOPEC VALPARAISO\tVALPARAISO\t01/01\t$ 35.000\n\nEjemplo Banco de Chile / BCI:\n18-10-2026\tCOMPRA COPEC MAIPU\t$ 25.000\n14-10-2026\tMERCADOPAGO*UBER\t$ 12.000`}
                    className="w-full text-xs font-mono p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50/50"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setMostrarModalMovimientos(false);
                      setTextoMovimientos('');
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={interpretarMovimientos}
                    disabled={procesandoMovimientos || !textoMovimientos.trim()}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
                  >
                    {procesandoMovimientos ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Analizando movimientos y verificando duplicados...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        Interpretar y Detectar Duplicados
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              /* Vista de resultados interpretados y detección de duplicados */
              <div className="space-y-4">
                {/* Métricas Resumen */}
                {(() => {
                  const total = movimientosData.length;
                  const duplicados = movimientosData.filter(m => m.es_duplicado).length;
                  const listos = movimientosData.filter(m => !m.es_duplicado).length;
                  const seleccionados = movimientosData.filter(m => m.seleccionado);
                  const montoSeleccionado = seleccionados.reduce((sum, m) => sum + (m.monto || 0), 0);

                  return (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-gray-50 border rounded-lg p-3 text-center">
                        <p className="text-[11px] text-gray-500 font-medium uppercase tracking-wider">Movimientos</p>
                        <p className="text-xl font-bold text-gray-800">{total}</p>
                      </div>

                      <div className={`border rounded-lg p-3 text-center ${
                        duplicados > 0 ? 'bg-amber-50/80 border-amber-300' : 'bg-gray-50'
                      }`}>
                        <div className="flex items-center justify-center gap-1">
                          <p className="text-[11px] text-amber-700 font-medium uppercase tracking-wider">Posibles Duplicados</p>
                          {duplicados > 0 && <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />}
                        </div>
                        <p className={`text-xl font-bold ${duplicados > 0 ? 'text-amber-800' : 'text-gray-400'}`}>
                          {duplicados}
                        </p>
                      </div>

                      <div className="bg-purple-50/60 border border-purple-200 rounded-lg p-3 text-center">
                        <p className="text-[11px] text-purple-700 font-medium uppercase tracking-wider">Nuevos a Ingresar</p>
                        <p className="text-xl font-bold text-purple-900">{listos}</p>
                      </div>

                      <div className="bg-indigo-50/80 border border-indigo-200 rounded-lg p-3 text-center">
                        <p className="text-[11px] text-indigo-700 font-medium uppercase tracking-wider">Total Seleccionado</p>
                        <p className="text-xl font-bold text-indigo-900">{formatCurrency(montoSeleccionado)}</p>
                      </div>
                    </div>
                  );
                })()}

                {/* Toolbar de filtros y acciones rápidas */}
                <div className="flex flex-wrap items-center justify-between gap-2 p-2 bg-gray-50 border rounded-lg">
                  {/* Pestañas de filtrado */}
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setFiltroMovimientos('todos')}
                      className={`text-xs px-2.5 py-1 rounded font-medium transition-colors ${
                        filtroMovimientos === 'todos'
                          ? 'bg-indigo-600 text-white shadow-sm'
                          : 'text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      Todos ({movimientosData.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setFiltroMovimientos('listos')}
                      className={`text-xs px-2.5 py-1 rounded font-medium transition-colors ${
                        filtroMovimientos === 'listos'
                          ? 'bg-indigo-600 text-white shadow-sm'
                          : 'text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      ✨ Nuevos ({movimientosData.filter(m => !m.es_duplicado).length})
                    </button>
                    <button
                      type="button"
                      onClick={() => setFiltroMovimientos('duplicados')}
                      className={`text-xs px-2.5 py-1 rounded font-medium transition-colors ${
                        filtroMovimientos === 'duplicados'
                          ? 'bg-amber-600 text-white shadow-sm'
                          : 'text-amber-800 hover:bg-amber-100'
                      }`}
                    >
                      ⚠️ Duplicados ({movimientosData.filter(m => m.es_duplicado).length})
                    </button>
                  </div>

                  {/* Acciones de selección / descarte */}
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {movimientosData.some(m => m.es_duplicado && m.seleccionado) && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={descartarDuplicadosMovimientos}
                        className="text-xs h-7 border-amber-300 text-amber-800 hover:bg-amber-100/70"
                        title="Desmarca automáticamente todos los gastos que ya existen en la app"
                      >
                        <CheckCheck className="w-3.5 h-3.5 mr-1 text-amber-700" />
                        Descartar Duplicados
                      </Button>
                    )}
                    <button
                      type="button"
                      onClick={() => seleccionarTodosMovimientos(true)}
                      className="text-xs text-indigo-700 hover:text-indigo-900 font-medium px-2 py-1 hover:bg-indigo-50 rounded"
                    >
                      Marcar todos
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={() => seleccionarTodosMovimientos(false)}
                      className="text-xs text-gray-600 hover:text-gray-800 font-medium px-2 py-1 hover:bg-gray-200 rounded"
                    >
                      Desmarcar todos
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      type="button"
                      onClick={() => setMovimientosData([])}
                      className="text-xs text-gray-500 hover:text-indigo-700 font-medium px-2 py-1 hover:bg-gray-200 rounded flex items-center gap-1"
                    >
                      <RotateCcw className="w-3 h-3" />
                      Pegar de nuevo
                    </button>
                    <span className="text-gray-300">|</span>
                    {/* Ordenar por Fecha o Monto */}
                    <div className="flex items-center gap-1 bg-white border border-gray-200 px-2 py-0.5 rounded-md text-xs shadow-xs">
                      <span className="text-[11px] text-gray-500 font-medium">Ordenar:</span>
                      <button
                        type="button"
                        onClick={() => cambiarOrden('fecha')}
                        className={`px-2 py-0.5 rounded text-[11px] font-medium flex items-center gap-1 transition-colors ${
                          ordenCampo === 'fecha'
                            ? 'bg-indigo-100 text-indigo-800 font-bold'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                        title="Ordenar por fecha"
                      >
                        Fecha
                        {ordenCampo === 'fecha' ? (
                          ordenDireccion === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : (
                          <ArrowUpDown className="w-2.5 h-2.5 text-gray-400" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => cambiarOrden('monto')}
                        className={`px-2 py-0.5 rounded text-[11px] font-medium flex items-center gap-1 transition-colors ${
                          ordenCampo === 'monto'
                            ? 'bg-indigo-100 text-indigo-800 font-bold'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                        title="Ordenar por monto"
                      >
                        Monto
                        {ordenCampo === 'monto' ? (
                          ordenDireccion === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                        ) : (
                          <ArrowUpDown className="w-2.5 h-2.5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Tabla de movimientos interactiva */}
                <div className="border rounded-lg overflow-hidden shadow-sm">
                  <div className="bg-indigo-700 text-white p-2 text-xs font-medium grid grid-cols-24 gap-1 items-center select-none">
                    <div className="col-span-1 text-center">Sel.</div>
                    <div className="col-span-3">
                      <button
                        type="button"
                        onClick={() => cambiarOrden('fecha')}
                        className="flex items-center gap-1 font-semibold text-white hover:text-indigo-200 transition-colors w-full text-left"
                        title="Ordenar por fecha"
                      >
                        <span>Fecha</span>
                        {ordenCampo === 'fecha' ? (
                          ordenDireccion === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-indigo-200" /> : <ArrowDown className="w-3.5 h-3.5 text-indigo-200" />
                        ) : (
                          <ArrowUpDown className="w-3 h-3 text-indigo-300 opacity-60 hover:opacity-100" />
                        )}
                      </button>
                    </div>
                    <div className="col-span-4">Detalle Banco</div>
                    <div className="col-span-4">Proveedor (Editable)</div>
                    <div className="col-span-3">Asignar a</div>
                    <div className="col-span-3">Categoría</div>
                    <div className="col-span-2 text-right">
                      <button
                        type="button"
                        onClick={() => cambiarOrden('monto')}
                        className="flex items-center gap-1 justify-end font-semibold text-white hover:text-indigo-200 transition-colors w-full text-right"
                        title="Ordenar por monto"
                      >
                        <span>Monto</span>
                        {ordenCampo === 'monto' ? (
                          ordenDireccion === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-indigo-200" /> : <ArrowDown className="w-3.5 h-3.5 text-indigo-200" />
                        ) : (
                          <ArrowUpDown className="w-3 h-3 text-indigo-300 opacity-60 hover:opacity-100" />
                        )}
                      </button>
                    </div>
                    <div className="col-span-3 text-center">Estado</div>
                    <div className="col-span-1 text-center"></div>
                  </div>

                  <div className="max-h-[380px] overflow-y-auto divide-y divide-gray-200">
                    {movimientosFiltradosYOrdenados.map((item, idx) => (
                        <div
                          key={item.id_temp}
                          className={`grid grid-cols-24 gap-1 p-2 items-center text-xs transition-colors ${
                            item.es_duplicado 
                              ? 'bg-amber-50/70 hover:bg-amber-100/60' 
                              : idx % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/60 hover:bg-gray-100'
                          } ${!item.seleccionado ? 'opacity-50' : ''}`}
                        >
                          {/* Checkbox selección */}
                          <div className="col-span-1 text-center">
                            <input
                              type="checkbox"
                              checked={item.seleccionado}
                              onChange={() => toggleSeleccionMovimiento(item.id_temp)}
                              className="w-4 h-4 text-indigo-600 rounded focus:ring-indigo-500 cursor-pointer"
                            />
                          </div>

                          {/* Fecha individual */}
                          <div className="col-span-3">
                            <input
                              type="date"
                              value={item.fecha}
                              onChange={(e) => actualizarFechaMovimiento(item.id_temp, e.target.value)}
                              className="w-full text-xs border rounded p-1 bg-white font-mono"
                              disabled={!item.seleccionado}
                            />
                          </div>

                          {/* Descripción original del banco */}
                          <div className="col-span-4">
                            <p className="font-medium text-gray-800 truncate text-[11px]" title={item.descripcion_raw || item.descripcion}>
                              {item.descripcion}
                            </p>
                            {item.descripcion_raw && item.descripcion_raw !== item.descripcion && (
                              <p className="text-[9px] text-gray-400 truncate" title={item.descripcion_raw}>
                                {item.descripcion_raw}
                              </p>
                            )}
                          </div>

                          {/* Nombre del Proveedor editable */}
                          <div className="col-span-4">
                            <input
                              type="text"
                              value={item.proveedor_nombre}
                              onChange={(e) => actualizarNombreProveedorMovimiento(item.id_temp, e.target.value)}
                              className="w-full text-xs border rounded p-1 font-medium bg-white"
                              disabled={!item.seleccionado}
                              placeholder="Nombre proveedor"
                            />
                          </div>

                          {/* Selector o Crear Nuevo Proveedor */}
                          <div className="col-span-3">
                            <SelectorProveedorCartola
                              valorId={item.proveedor_id || (item.crear_nuevo_proveedor ? 'nuevo' : 'nuevo')}
                              nombreCartola={item.descripcion}
                              nombreEditable={item.proveedor_nombre}
                              proveedores={proveedoresOrdenados}
                              categorias={categoriasOrdenadas}
                              disabled={!item.seleccionado}
                              onSelectProveedor={(id) => seleccionarProveedorMovimiento(item.id_temp, id)}
                              onMarcarNuevo={() => marcarCrearNuevoProveedorMovimiento(item.id_temp)}
                            />
                          </div>

                          {/* Categoría inteligente */}
                          <div className="col-span-3">
                            {(() => {
                              const { categorias: catsItem, esFiltrado } = obtenerCategoriasItemMovimiento(item);
                              return (
                                <select
                                  value={item.categoria_id || ''}
                                  onChange={(e) => {
                                    if (e.target.value === '__ver_todas__') {
                                      setMostrarTodasCategoriasMov(prev => ({ ...prev, [item.id_temp]: true }));
                                    } else {
                                      actualizarCategoriaMovimiento(item.id_temp, e.target.value);
                                    }
                                  }}
                                  className={`w-full text-xs border rounded p-1 bg-white ${
                                    item.seleccionado && !item.categoria_id ? 'border-red-300 bg-red-50' : ''
                                  }`}
                                  disabled={!item.seleccionado}
                                >
                                  <option value="">Categoría...</option>
                                  {catsItem.map(cat => (
                                    <option key={cat.id_categoria} value={cat.id_categoria}>
                                      {cat.nombre}
                                    </option>
                                  ))}
                                  {esFiltrado && (
                                    <option value="__ver_todas__">➕ Ver todas ({categoriasOrdenadas.length})...</option>
                                  )}
                                </select>
                              );
                            })()}
                          </div>

                          {/* Monto */}
                          <div className="col-span-2 text-right font-semibold text-indigo-900">
                            {formatCurrency(item.monto)}
                          </div>

                          {/* Estado / Duplicado */}
                          <div className="col-span-3 text-center">
                            {item.es_duplicado ? (
                              <div className="flex items-center justify-center gap-1">
                                <span
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-900 border border-amber-300 truncate max-w-[130px]"
                                  title={item.motivo_duplicado || 'Gasto similar ya registrado en la app'}
                                >
                                  <AlertTriangle className="w-3 h-3 text-amber-700 shrink-0" />
                                  <span className="truncate">Ya en app</span>
                                </span>
                                {item.seleccionado && (
                                  <button
                                    type="button"
                                    onClick={() => toggleSeleccionMovimiento(item.id_temp)}
                                    className="text-[10px] text-amber-800 underline hover:text-amber-950 font-medium"
                                    title="Descartar este gasto duplicado"
                                  >
                                    Descartar
                                  </button>
                                )}
                              </div>
                            ) : (
                              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-800">
                                ✨ Nuevo
                              </span>
                            )}
                          </div>

                          {/* Botón eliminar fila */}
                          <div className="col-span-1 text-center">
                            <button
                              type="button"
                              onClick={() => eliminarMovimiento(item.id_temp)}
                              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Eliminar de la lista"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>

                {/* Barra de pie con resumen y confirmación */}
                <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t bg-gray-50/50 p-3 rounded-lg">
                  <div className="text-xs text-gray-600">
                    <span>Propiedad: <strong>{propiedades.find(p => p.id_propiedad === propiedadIdMovimientos)?.nombre || 'Seleccionada'}</strong></span>
                    <span className="mx-2">•</span>
                    <span>Medio de pago: <strong>{mediosPago.find(m => m.id_medio_pago === medioPagoGlobalMovimientos)?.nombre || 'Por defecto'}</strong></span>
                    <span className="mx-2">•</span>
                    <span>Gastos seleccionados: <strong className="text-indigo-800 font-bold">{movimientosData.filter(m => m.seleccionado).length} de {movimientosData.length}</strong></span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setMostrarModalMovimientos(false)}
                      disabled={creandoGastosMovimientos}
                    >
                      Cerrar
                    </Button>
                    <Button
                      onClick={crearGastosDesdeMovimientos}
                      disabled={creandoGastosMovimientos || movimientosData.filter(m => m.seleccionado).length === 0}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium"
                    >
                      {creandoGastosMovimientos ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Ingresando gastos...
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4 mr-1.5" />
                          Crear {movimientosData.filter(m => m.seleccionado).length} Gastos Seleccionados
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GastoRapido;
